import { useState, useSyncExternalStore } from "react";
import { MapContainer, TileLayer } from "react-leaflet";
import { useLanguage, useT } from "../i18n/hooks.ts";
import { driveSyncStore } from "../model/driveSyncStore.ts";
import type { ProjectMeta } from "../model/storage.ts";
import type { ExampleProject } from "../model/examples.ts";
import type { Language } from "../model/language.ts";
import { BASEMAP_ATTRIBUTION, BASEMAP_URL } from "./basemap.ts";
import { FileButton } from "./FileButton.tsx";
import { RenameDialog } from "./RenameDialog.tsx";
import { Logo, Wordmark } from "./Logo.tsx";

/** Small per-row indicator of `p`'s Google Drive sync status — nothing shown for "not-synced" (the default/neutral state) or while Drive isn't connected at all. */
function DriveSyncStatus({ projectId }: { projectId: string }) {
  const t = useT();
  const state = useSyncExternalStore(driveSyncStore.subscribe, () =>
    driveSyncStore.getProjectSyncState(projectId)
  );
  if (state.status === "synced") {
    return (
      <span className="landing-saved-drive-status" title={t("driveSync.statusSynced")}>
        ☁
      </span>
    );
  }
  if (state.status === "syncing") {
    return (
      <span className="landing-saved-drive-status" title={t("driveSync.statusSyncing")}>
        ⋯
      </span>
    );
  }
  if (state.status === "error") {
    return (
      <span className="landing-saved-drive-status" title={t("driveSync.statusError")}>
        ⚠
      </span>
    );
  }
  return null;
}

/**
 * The row's one secondary-action button: normally "rename", but a project
 * flagged as a Drive sync conflict (Drive has a meaningfully newer copy,
 * see driveSyncStore.ts's `reconcile`) needs resolving first — this swaps
 * to "use the Drive version" instead until that's done, rather than adding
 * a second button competing for the same small slot. Unlike the rename
 * pencil (only visible on hover, see .landing-saved-edit in styles.css), a
 * conflict stays visible: `.landing-saved-conflict` overrides that.
 */
function SavedProjectEditButton({
  project,
  loading,
  onRename,
  onResolveConflict,
}: {
  project: ProjectMeta;
  loading: boolean;
  onRename: () => void;
  onResolveConflict: (id: string) => void;
}) {
  const t = useT();
  const status = useSyncExternalStore(
    driveSyncStore.subscribe,
    () => driveSyncStore.getProjectSyncState(project.id).status
  );
  if (status === "conflict") {
    return (
      <button
        className="button-icon landing-saved-edit landing-saved-conflict"
        onClick={() => onResolveConflict(project.id)}
        disabled={loading}
        aria-label={t("driveSync.useDriveVersion")}
        title={t("driveSync.conflictTooltip")}
      >
        ⚠
      </button>
    );
  }
  return (
    <button
      className="button-icon landing-saved-edit"
      onClick={onRename}
      disabled={loading}
      aria-label={t("landing.renameAria", { name: project.name })}
      title={t("landing.rename")}
    >
      ✎
    </button>
  );
}

// Decorative center (the Turia basin) — purely visual, doesn't depend on any project.
const DECORATIVE_CENTER: [number, number] = [39.85, -0.9];

// en-GB (not en-US) on purpose: keeps the same day/month order as the rest
// of the app in Spanish, instead of switching to month/day like en-US would.
const LOCALE_TAGS: Record<Language, string> = { es: "es-ES", en: "en-GB" };

function formatDate(language: Language, timestamp: number): string {
  return new Date(timestamp).toLocaleString(LOCALE_TAGS[language], {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function Landing({
  loading,
  error,
  savedProjects,
  onOpen,
  onCreateNew,
  onOpenSaved,
  onRenameSaved,
  onImport,
  onResolveDriveConflict,
  examples,
  onLoadExample,
}: {
  loading: boolean;
  error: string | null;
  /** Everything opened before ends up here — see storage.ts. The original folder is no longer needed to reopen them. */
  savedProjects: ProjectMeta[];
  onOpen: () => void;
  onCreateNew: () => void;
  onOpenSaved: (id: string) => void;
  onRenameSaved: (id: string, name: string) => void;
  /** Uploads an .ehdz exported earlier (see exportProject.ts) and opens it as a new saved project. */
  onImport: (file: File) => void;
  /** "Use the Drive version" for a project flagged as a sync conflict (see driveSyncStore.ts) — re-downloads and overwrites the local copy. */
  onResolveDriveConflict: (id: string) => void;
  examples: ExampleProject[];
  onLoadExample: (ejemplo: ExampleProject) => void;
}) {
  const t = useT();
  const language = useLanguage();
  const [renaming, setRenaming] = useState<ProjectMeta | null>(null);
  const driveConnected = useSyncExternalStore(
    driveSyncStore.subscribe,
    () => driveSyncStore.getSnapshot().connected
  );
  // Chromium-only (Chrome/Edge) — same check useProjectPersistence.ts's own
  // openFolder() already makes before actually calling showDirectoryPicker().
  // That guard already surfaces a real, translated error if this button is
  // clicked on an unsupported browser (Firefox/Safari) — this only adds
  // upfront visibility, so a Firefox/Safari user sees a disabled button with
  // an explanation instead of clicking into a dead end first. Real gap
  // found by deep-review (2026-08-29).
  const fileSystemAccessSupported = "showDirectoryPicker" in window;

  return (
    <div className="landing">
      <div className="landing-map">
        <MapContainer
          center={DECORATIVE_CENTER}
          zoom={8}
          zoomControl={false}
          dragging={false}
          scrollWheelZoom={false}
          doubleClickZoom={false}
          touchZoom={false}
          boxZoom={false}
          keyboard={false}
          attributionControl={false}
        >
          <TileLayer attribution={BASEMAP_ATTRIBUTION} url={BASEMAP_URL} />
        </MapContainer>
      </div>
      <div className="landing-veil" />

      <div className="landing-card">
        <div className="landing-brand">
          <Logo size={56} />
          <h1>
            <Wordmark size={28} />
          </h1>
        </div>
        <p className="landing-tagline">{t("landing.tagline")}</p>

        {savedProjects.length > 0 && (
          <div className="landing-saved">
            <p className="landing-examples-title">
              {t("landing.yourProjects")}
              {!driveConnected && (
                <span
                  className="landing-drive-hint"
                  title={t("landing.driveNotConnectedHint")}
                  aria-label={t("landing.driveNotConnectedHint")}
                >
                  ⓘ
                </span>
              )}
            </p>
            <ul>
              {savedProjects.map((p) => (
                <li key={p.id} className="landing-saved-row">
                  <button
                    className="landing-saved-button"
                    onClick={() => onOpenSaved(p.id)}
                    disabled={loading}
                    title={t("landing.openProject", { name: p.name })}
                  >
                    <span className="landing-saved-name">{p.name}</span>
                    <span className="landing-saved-date">
                      {formatDate(language, p.lastModified)}
                      {driveConnected && <DriveSyncStatus projectId={p.id} />}
                    </span>
                  </button>
                  <SavedProjectEditButton
                    project={p}
                    loading={loading}
                    onRename={() => setRenaming(p)}
                    onResolveConflict={onResolveDriveConflict}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="landing-actions">
          <button
            className="button button-primary"
            onClick={onOpen}
            disabled={loading || !fileSystemAccessSupported}
            title={fileSystemAccessSupported ? undefined : t("app.noFileSystemAccessApiError")}
          >
            {loading ? t("landing.loading") : t("landing.openFromFolder")}
          </button>
          <div className="landing-actions-secondary">
            <button className="button" onClick={onCreateNew} disabled={loading}>
              {t("landing.createNew")}
            </button>
            <FileButton
              accept=".ehdz"
              disabled={loading}
              label={t("landing.importCopy")}
              onFiles={(files) => {
                const file = files?.[0];
                if (file) onImport(file);
              }}
            />
          </div>
        </div>

        {error && <p className="error">{error}</p>}

        <div className="landing-examples">
          <p className="landing-examples-title">{t("landing.orTryAnExample")}</p>
          <ul>
            {examples.map((ej) => (
              <li key={ej.id}>
                <button
                  className="landing-example"
                  onClick={() => onLoadExample(ej)}
                  disabled={loading}
                  title={t(ej.description)}
                >
                  {t(ej.title)}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {renaming && (
        <RenameDialog
          title={t("topbar.renameDialogTitle")}
          initialName={renaming.name}
          onSave={(name) => {
            onRenameSaved(renaming.id, name);
            setRenaming(null);
          }}
          onClose={() => setRenaming(null)}
        />
      )}
    </div>
  );
}
